import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Building2,
  Loader2,
  Users,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useDataMode } from '@/contexts/DataModeContext';
import { resourcesApi } from '@/services/api';
import type { ResourceCompanyAccessDto } from '@/services/api';
import { mockIntegrationsService } from '@/data/mockIntegrationsData';
import type { EmployeeCompanyAccessRow } from '@/types/integrations';

interface BulkResource {
  id: string;
  name: string;
}

interface BulkUpdateCompanyAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedResources: BulkResource[];
}

type CompanyRow = {
  companyId: string;
  companyName: string;
  companyCode: string;
  hasAccess: boolean;
  isDefault: boolean;
};

export const BulkUpdateCompanyAccessDialog: React.FC<BulkUpdateCompanyAccessDialogProps> = ({
  open,
  onOpenChange,
  selectedResources,
}) => {
  const { getScreenMode } = useDataMode();
  const mode = getScreenMode('resources');

  const [companyRows, setCompanyRows] = useState<CompanyRow[]>([]);
  const [companyUpdates, setCompanyUpdates] = useState<Record<string, 'grant' | 'revoke' | 'no-change'>>({});
  const [defaultCompanyId, setDefaultCompanyId] = useState<string | null>(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCompanyUpdates({});
    setDefaultCompanyId(null);
    loadCompanies();
  }, [open]);

  const loadCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      const firstId = selectedResources[0]?.id;
      if (!firstId) return;

      let rows: CompanyRow[];
      if (mode === 'demo') {
        const data = await mockIntegrationsService.getEmployeeCompanyAccess(firstId);
        rows = data.map((r: EmployeeCompanyAccessRow) => ({
          companyId: r.companyId,
          companyName: r.companyName,
          companyCode: r.companyCode,
          hasAccess: r.hasAccess,
          isDefault: r.isDefault,
        }));
      } else {
        const data = await resourcesApi.getCompanyAccess(firstId);
        rows = data.map((r: ResourceCompanyAccessDto) => ({
          companyId: r.companyId,
          companyName: r.companyName,
          companyCode: r.companyCode,
          hasAccess: r.hasAccess,
          isDefault: r.isDefault,
        }));
      }

      setCompanyRows(rows);
      const initialUpdates: Record<string, 'grant' | 'revoke' | 'no-change'> = {};
      rows.forEach((r) => { initialUpdates[r.companyId] = 'no-change'; });
      setCompanyUpdates(initialUpdates);
    } catch {
      toast.error('Failed to load companies');
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const changedCompanies = Object.entries(companyUpdates).filter(([, v]) => v !== 'no-change');
  const hasCompanyChanges = changedCompanies.length > 0;

  const cycleCompanyAction = (companyId: string) => {
    setCompanyUpdates((prev) => {
      const current = prev[companyId] ?? 'no-change';
      const next =
        current === 'no-change' ? 'grant' : current === 'grant' ? 'revoke' : 'no-change';
      return { ...prev, [companyId]: next };
    });
  };

  const actionLabel = (action: 'grant' | 'revoke' | 'no-change') => {
    if (action === 'grant') return { label: 'Grant', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400' };
    if (action === 'revoke') return { label: 'Revoke', cls: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400' };
    return { label: 'No change', cls: 'bg-muted text-muted-foreground border-border' };
  };

  const handleSubmit = async () => {
    if (!hasCompanyChanges) return;
    setIsSubmitting(true);
    try {
      const grants = changedCompanies.filter(([, v]) => v === 'grant');
      const revokes = changedCompanies.filter(([, v]) => v === 'revoke');
      const failures: string[] = [];
      let successCount = 0;

      if (mode === 'live') {
        for (const res of selectedResources) {
          try {
            const resAccess = await resourcesApi.getCompanyAccess(res.id);
            const resAccessMap = new Map(resAccess.map((r) => [r.companyId, r]));

            for (const [companyId, action] of changedCompanies) {
              const existing = resAccessMap.get(companyId);
              const currentHasAccess = existing?.hasAccess ?? false;

              if (action === 'grant' && !currentHasAccess) {
                const isDefault = defaultCompanyId === companyId;
                await resourcesApi.setCompanyAccess(res.id, companyId, isDefault);
              } else if (action === 'revoke' && currentHasAccess) {
                await resourcesApi.removeCompanyAccess(res.id, companyId);
              } else if (action === 'grant' && currentHasAccess && defaultCompanyId === companyId) {
                await resourcesApi.setDefaultCompany(res.id, companyId);
              }
            }

            if (defaultCompanyId && companyUpdates[defaultCompanyId] !== 'grant') {
              const existingDefault = resAccessMap.get(defaultCompanyId);
              if (existingDefault?.hasAccess) {
                await resourcesApi.setDefaultCompany(res.id, defaultCompanyId);
              }
            }

            successCount++;
          } catch {
            failures.push(`${res.name}: company access update failed`);
          }
        }
      } else {
        for (const res of selectedResources) {
          try {
            const accessUpdates = companyRows.map((row) => {
              const action = companyUpdates[row.companyId] ?? 'no-change';
              return {
                companyId: row.companyId,
                hasAccess:
                  action === 'grant' ? true : action === 'revoke' ? false : row.hasAccess,
                isDefault:
                  defaultCompanyId != null
                    ? defaultCompanyId === row.companyId
                    : row.isDefault,
              };
            });
            await mockIntegrationsService.updateEmployeeCompanyAccess(res.id, accessUpdates);
            successCount++;
          } catch {
            failures.push(`${res.name}: company access update failed`);
          }
        }
      }

      const parts: string[] = [];
      if (grants.length) parts.push(`${grants.length} compan${grants.length === 1 ? 'y' : 'ies'} granted`);
      if (revokes.length) parts.push(`${revokes.length} compan${revokes.length === 1 ? 'y' : 'ies'} revoked`);
      if (successCount < selectedResources.length) {
        parts.push(`${successCount}/${selectedResources.length} succeeded`);
      }

      if (failures.length > 0) {
        toast.warning(
          `Partially updated ${selectedResources.length} resource${selectedResources.length === 1 ? '' : 's'}`,
          { description: [...parts, ...failures].join(' \u2022 ') }
        );
      } else {
        toast.success(
          `Updated ${selectedResources.length} resource${selectedResources.length === 1 ? '' : 's'}`,
          { description: parts.join(' \u2022 ') }
        );
      }

      window.dispatchEvent(new CustomEvent('company-access-changed'));
      onOpenChange(false);
    } catch {
      toast.error('Failed to apply bulk updates');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] h-[90vh] max-h-[720px] flex flex-col gap-0 p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-base">Bulk Update Company Access</DialogTitle>
              <DialogDescription className="text-sm mt-0.5">
                Applying changes to{' '}
                <span className="font-semibold text-foreground">
                  {selectedResources.length} resource{selectedResources.length === 1 ? '' : 's'}
                </span>
              </DialogDescription>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-3">
            {selectedResources.slice(0, 6).map((res) => (
              <Badge key={res.id} variant="secondary" className="text-xs font-normal">
                {res.name}
              </Badge>
            ))}
            {selectedResources.length > 6 && (
              <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                +{selectedResources.length - 6} more
              </Badge>
            )}
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {isLoadingCompanies ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <div className="space-y-1">
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                  <Skeleton className="h-7 w-24 rounded-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-1">
              <Alert className="mb-3">
                <AlertDescription className="text-xs">
                  Click a company row to cycle through: <strong>No change</strong> &rarr; <strong>Grant</strong> &rarr; <strong>Revoke</strong>
                </AlertDescription>
              </Alert>

              {companyRows.map((row) => {
                const update = companyUpdates[row.companyId] ?? 'no-change';
                const { label, cls } = actionLabel(update);
                return (
                  <button
                    key={row.companyId}
                    type="button"
                    onClick={() => cycleCompanyAction(row.companyId)}
                    className={cn(
                      'w-full flex items-center justify-between py-2.5 px-3 rounded-lg transition-all text-left',
                      'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      update !== 'no-change' && 'ring-1 ring-inset ring-border bg-muted/30'
                    )}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted flex-shrink-0">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{row.companyName}</p>
                        <p className="text-xs text-muted-foreground">{row.companyCode}</p>
                      </div>
                    </div>

                    <Badge
                      variant="outline"
                      className={cn('text-xs font-medium flex-shrink-0', cls)}
                    >
                      {update === 'grant' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                      {update === 'revoke' && <AlertTriangle className="h-3 w-3 mr-1" />}
                      {label}
                    </Badge>
                  </button>
                );
              })}

              {companyRows.some((r) => companyUpdates[r.companyId] === 'grant') && (
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Set default company (optional)
                  </p>
                  <div className="space-y-1">
                    {companyRows
                      .filter((r) => companyUpdates[r.companyId] === 'grant')
                      .map((row) => (
                        <label
                          key={row.companyId}
                          className="flex items-center gap-2.5 py-2 px-3 rounded-lg hover:bg-muted/40 cursor-pointer"
                        >
                          <Checkbox
                            checked={defaultCompanyId === row.companyId}
                            onCheckedChange={(checked) =>
                              setDefaultCompanyId(checked ? row.companyId : null)
                            }
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{row.companyName}</p>
                            <p className="text-xs text-muted-foreground">{row.companyCode}</p>
                          </div>
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-muted/30 gap-2">
          <div className="flex-1 text-xs text-muted-foreground">
            {hasCompanyChanges ? (
              <span className="flex items-center gap-1.5 text-foreground font-medium">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                {changedCompanies.length} company change{changedCompanies.length !== 1 ? 's' : ''} ready
              </span>
            ) : (
              <span>No changes selected</span>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!hasCompanyChanges || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>Apply to {selectedResources.length} resource{selectedResources.length !== 1 ? 's' : ''}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
